import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { SecondaryButton } from "../UI";
import SuccessAlert from '../UI/Alert/SuccessAlert';
import EditIcon from '../../assets/icons/edit.svg';
import Loader from '../UI/Loader/Loader';
import backwardArrow from '../../assets/icons/backward-arrow.svg';
import { useUserContext } from '../../contexts/UserContext';
import { updateOrgLevels } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const OrgLevelDetails = () => {
    const navigate = useNavigate()
    const location = useLocation();
    const { content } = useContentfulContext();

    const { userData } = useUserContext()
    const { data, property } = location.state || {};

    const [jsonCode, setJsonCode] = useState('');
    const [enableEdit, setEnableEdit] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");

    useEffect(() => {
        if (data && property && data?.[property] !== undefined) {
            setJsonCode(JSON.stringify(data[property], null, 2));
        } else {
            setJsonCode('');
        }
    }, [data, property]);

    const handleJsonChange = (newCode) => {
        setJsonCode(newCode);
        try {
            JSON.parse(newCode);
            setValidationError('');
        } catch (error) {
            setValidationError('Invalid JSON format');
        }
    };

    const editOrgLevels = async (id, payload) => {
        try {
            setLoading(true);
            const res = await updateOrgLevels(userData?.token, userData?.selectedOrganization?.org_token, id, payload);
            return res
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error('An error occurred while updating the org level.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const parsedData = JSON.parse(jsonCode);
            if (!property) {
                throw new Error("Property is not defined.");
            }
            const updatedPayload = { [property]: parsedData };
            const res = await editOrgLevels(data._id, updatedPayload)
            if(res.status){
                setSuccess(res.message)
                setTimeout(() => setSuccess(''), content.ORGLEVELS.timeout);
            }
            setValidationError('');
        } catch (error) {
            setValidationError(error.message || 'Invalid JSON format. Please correct it before saving.');
        }
    };

    return (
        <div class="my-4 px-8 w-full">

            <a href="#" class="font-medium text-primary hover:underline flex items-center" onClick={(e) => {
                e.preventDefault();
                navigate("/admin/org-level/")
            }}>
                <img src={backwardArrow} alt='' className='w-5 h-5 mr-2' /> {content.ORGLEVELS.back_cta_text}
            </a>

            <div className="w-full h-screen flex flex-col space-y-4 mt-2 mb-6 ">

                <div className="flex gap-4 items-baseline space-y-2 mb-6">
                    <h1 class="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">Organization Level {data?.id || ""} : {`${content.ORGLEVELS.table_headers?.[property]}`} Configuration</h1>
                    {!enableEdit && (
                        <img
                            src={EditIcon}
                            className="w-[20px] cursor-pointer"
                            onClick={() => setEnableEdit(true)}
                            alt="Edit"
                            title="Edit"
                        />
                    )}
                </div>

                {
                    loading && <Loader />
                }
                {success && (
                    <SuccessAlert disappearTime={content.ORGLEVELS.timeout}>
                        {success}
                    </SuccessAlert>
                )}

                <div className="flex-grow overflow-y-auto">
                    <CodeMirror
                        value={jsonCode}
                        height="100%"
                        readOnly={!enableEdit}
                        editable={enableEdit}
                        lineWrapping={true}
                        extensions={[EditorView.lineWrapping]}
                        onChange={handleJsonChange}
                    />
                </div>


                <div className="sticky bottom-0 bg-white p-6 border-t">
                    {validationError && (
                        <div className="text-base leading-tight tracking-tight text-red-500 mb-4">
                            {validationError}
                        </div>
                    )}
                    {enableEdit && (
                        <SecondaryButton handleClick={handleSave}>
                            Update configuration
                        </SecondaryButton>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrgLevelDetails;

